import { renumberRows, type DraftRowShape } from '@/lib/draft-rows/draftRow';

/**
 * Display rows for a surface step whose draft has been touched: the user's
 * working drafts, PLUS server-seeded rows they haven't pulled in yet (e.g. a
 * role minted on the Mitarbeiter step after this table was captured). Drafts
 * win on conflict — a row edited locally is matched by `entityId` and never
 * overwritten by its server copy — so a background refetch surfaces genuinely
 * new roles without clobbering edits.
 *
 * A draft row WITHOUT an id is a create still in flight (or awaiting a fix):
 * its server copy — refetched before the flush response stamped the id, or an
 * entity the row will convert into an update on the next flush — must not
 * render next to it. `pendingKeyOf` names that display-only match (normalized
 * name); the id-stamping converges the two, this just keeps the frame in
 * between from doubling the row.
 *
 * An untouched draft (empty rows) is handled by the caller, which shows the
 * seeds directly; this runs only once the draft owns the table.
 */
export function mergeSurfaceRows<TData extends object, TRow extends DraftRowShape<TData>>(
  draftRows: TRow[],
  existingRows: TRow[],
  pendingKeyOf: (data: TData) => string
): TRow[] {
  const draftEntityIds = new Set(draftRows.map((row) => row.entityId).filter(Boolean));
  const pendingKeys = new Set(
    draftRows
      .filter((row) => !row.entityId)
      .map((row) => pendingKeyOf(row.data))
      .filter(Boolean)
  );
  const additions = existingRows.filter(
    (row) =>
      row.entityId && !draftEntityIds.has(row.entityId) && !pendingKeys.has(pendingKeyOf(row.data))
  );
  return additions.length === 0 ? draftRows : renumberRows([...draftRows, ...additions]);
}
